import { ArrowLeft, ChartNoAxesColumn, Minus, Play, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar } from '../../components/Avatar.tsx';
import { BasculeNet } from '../../components/BasculeNet.tsx';
import { Podium } from '../../components/Podium.tsx';
import { ApiError } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import type { CarteTour } from '../../lib/rounds.ts';
import { deleteRound, getCarteTour, saveHole } from '../../lib/rounds.ts';
import { rendusDuJoueur, useScoreNet } from '../../lib/scoreNet.ts';
import { Carte } from './Carte.tsx';

/* La carte du tour, celle de tout le monde.
 *
 * ── Pourquoi elle vient après la partie, et pas pendant ────────────────────
 *
 * En marchant, le détail trou par trou des autres n'intéresse personne et
 * tiendrait la moitié de l'écran. Au club-house c'est l'inverse : on compare,
 * et c'est même tout ce qu'on fait.
 *
 * ── Pourquoi on peut encore corriger ───────────────────────────────────────
 *
 * On s'aperçoit d'un trou mal compté en refaisant l'addition, pas sur le
 * green. Une carte qu'on ne peut plus toucher devient une carte qu'on cesse de
 * tenir — et un score faux se propage ensuite dans la moyenne du trou et le
 * meilleur score.
 *
 * On ne corrige QUE la sienne. Celle des autres se regarde.
 */

const jour = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

export function RoundCard() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [scoreNet, setScoreNet] = useScoreNet();
    const [carte, setCarte] = useState<CarteTour | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [corrige, setCorrige] = useState<number | null>(null);
    const [confirmSuppr, setConfirmSuppr] = useState(false);

    const charger = (): void => {
        if (!id) return;
        void getCarteTour(id)
            .then(setCarte)
            .catch((e) => setError(e instanceof ApiError ? e.message : 'Chargement impossible'))
            .finally(() => setLoading(false));
    };
    useEffect(charger, [id]);

    const moi = carte?.players.find((j) => j.userId === user?.id) ?? null;

    const ecrire = async (n: number, strokes: number | null, putts: number): Promise<void> => {
        if (!id) return;
        setBusy(true);
        setError(null);
        try {
            await saveHole(id, n, { strokes, putts: strokes == null ? null : putts });
            setCarte((c) => (c == null ? c : {
                ...c,
                players: c.players.map((j) => (j.userId !== user?.id ? j : {
                    ...j,
                    holes: [
                        ...j.holes.filter((h) => h.holeNumber !== n),
                        { holeNumber: n, strokes, putts: strokes == null ? null : putts },
                    ].sort((a, b) => a.holeNumber - b.holeNumber),
                })),
            }));
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Correction impossible');
        } finally { setBusy(false); }
    };

    const supprimer = async (): Promise<void> => {
        if (!id) return;
        setBusy(true);
        try {
            await deleteRound(id);
            navigate('/parcours/historique');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }
    if (!carte) {
        return <div role="alert" className="alert alert-error m-4 text-sm"><span>{error ?? 'Tour introuvable'}</span></div>;
    }

    /* Les coups rendus de CHAQUE joueur, depuis SON départ.
     *
     * Deux partenaires peuvent partir de repères différents et n'avoir ni le
     * même index ni le même slope : un calcul commun au tour serait faux pour
     * au moins l'un des deux. */
    const rendusDe = new Map(
        carte.players.map((j) => [
            j.userId,
            rendusDuJoueur(j, carte.holes, carte.round.maxHandicapStrokes),
        ]),
    );
    /** Le net est-il lisible pour tout le monde ? Un seul joueur sans index
     *  suffit à rendre un classement net trompeur : on s'en tient au brut. */
    const netPossible = carte.players.every((j) => {
        const r = rendusDe.get(j.userId);
        return r != null && r.total != null && r.indexConnus;
    });
    const enNet = scoreNet && netPossible;

    const totalDe = (j: CarteTour['players'][number]): {
        coups: number; brut: number; par: number;
    } => {
        const r = rendusDe.get(j.userId);
        const brut = j.holes.reduce((s, h) => s + (h.strokes ?? 0), 0);
        const rendus = enNet
            ? j.holes.reduce(
                (s, h) => s + (h.strokes == null ? 0 : r?.parTrou.get(h.holeNumber) ?? 0),
                0,
            )
            : 0;
        return {
            coups: brut - rendus,
            brut,
            par: carte.holes
                .filter((h) => j.holes.some((x) => x.holeNumber === h.number && x.strokes != null))
                .reduce((s, h) => s + (h.par ?? 0), 0),
        };
    };

    // Classement à l'écart au par : après cinq trous, 22 coups et 18 coups ne
    // se comparent que si l'on sait qui en a joué combien.
    const classes = [...carte.players].sort((a, b) => {
        const ta = totalDe(a); const tb = totalDe(b);
        if (ta.coups === 0) return 1;
        if (tb.coups === 0) return -1;
        return (ta.coups - ta.par) - (tb.coups - tb.par);
    });

    /* Ce qui manque pour lire un net, dit une fois et en clair.
     *
     * Le carnet sans index de difficulté est le cas courant, et c'est le joueur
     * qui peut le corriger — d'où la phrase qui le désigne plutôt qu'un « net
     * indisponible » dont on ne saurait que faire. */
    const manqueQuoi = netPossible ? '' : (
        carte.holes.some((h) => h.strokeIndex == null)
            ? 'Net impossible : le carnet n’a pas l’index des trous'
            : 'Net impossible : un joueur n’a pas d’index'
    );

    /* Le net est-il APPROCHÉ ?
     *
     * Les coups reçus se calculent avec le slope et le SSS du départ. Sans eux,
     * la formule retombe sur le cas neutre et rend l'index tel quel — ce qui est
     * la même formule, mais pas le même nombre. L'écart n'a rien de théorique :
     * sur un parcours difficile il vaut plusieurs coups, et le net affiché ne
     * sera pas celui de la fédération.
     *
     * Le drapeau existait depuis le premier jour et n'était montré nulle part :
     * l'écran présentait donc un net approché comme s'il faisait foi. */
    const netApproche = carte.players.some(
        (j) => rendusDe.get(j.userId)?.approche ?? false,
    );

    const trouCorrige = corrige == null ? null : carte.holes.find((h) => h.number === corrige) ?? null;
    const scoreCorrige = corrige == null ? null : moi?.holes.find((h) => h.holeNumber === corrige) ?? null;

    /* Trois conditions à la fois — la partie est TERMINÉE, il y a plus d'un
     * joueur, et au moins l'un d'eux a un score — parce qu'un podium sur un
     * tour encore ouvert, joué seul, ou sans une seule carte saisie ne
     * classerait rien de vrai. */
    const podium = carte.round.status === 'finished' && classes.length > 1
        ? classes.filter((j) => totalDe(j).coups > 0).slice(0, 3)
        : [];

    return (
        <div className="space-y-4 py-2">
            <Link to="/parcours/historique" className="btn btn-ghost btn-sm -mb-2 gap-2">
                <ArrowLeft size={16} /> Mes parcours
            </Link>

            {/* L'entête est CENTRÉE, et le podium juste dessous.
                Alignée à gauche, elle se lisait comme le haut d'une liste — la
                même forme que dix autres écrans de l'application. Centrée, avec
                le podium sous elle, la page devient une affiche : c'est le
                moment où l'on montre son téléphone aux autres, pas celui où l'on
                consulte. */}
            <header className="text-center">
                {/* Le nom du parcours passe en surtitre, et « Partie terminée »
                    prend la grande ligne.

                    C'est l'écran où l'on arrive en rendant sa carte, et ce qu'on
                    veut y lire d'abord n'est pas où l'on était — on le sait, on
                    en revient — mais que c'est fini. Le parcours et le nombre
                    de trous restent juste au-dessus, en petites capitales : ils
                    situent sans occuper la ligne principale.

                    Un tour encore ouvert, lui, garde son nom en titre : il n'y
                    a rien à annoncer, on est juste venu regarder. */}
                <p className="text-xs font-bold uppercase tracking-[0.16em]"
                    style={{ color: 'var(--tuile-encre)' }}>
                    {carte.round.courseName}
                    <span className="opacity-60">{' · '}{carte.holes.length} trous</span>
                </p>
                <h1 className="font-display text-3xl font-bold leading-tight tracking-tight">
                    {carte.round.myFinished ? 'Partie terminée' : carte.round.courseName}
                </h1>
                <p className="mt-0.5 text-xs opacity-60">
                    {jour(carte.round.startedAt)}
                    {moi?.teeLabel && ` · départ ${moi.teeLabel}`}
                    {/* « non terminé » parle de MA carte, pas du tour : un
                        partenaire qui joue encore ne rend pas la mienne
                        inachevée. */}
                    {!carte.round.myFinished && ' · non terminé'}
                    {carte.round.myFinished && carte.round.status === 'open'
                        && ' · vos partenaires jouent encore'}
                </p>
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {/* Le même podium qu'à la fin d'une partie de putting : mêmes
                métaux, même ordre 2-1-3, même estrade. Deux façons de finir une
                partie ne doivent pas donner deux façons de gagner. */}
            <Podium moiId={user?.id}
                joueurs={podium.map((j) => ({
                    userId: j.userId,
                    displayName: j.displayName,
                    avatarUrl: j.avatarUrl,
                    valeur: String(totalDe(j).coups),
                }))} />

            {/* La bascule, juste au-dessus des cartes qu'elle change.
                Absente quand le net n'est pas calculable : un choix sans effet
                vaut moins que pas de choix. La raison est dite à la place. */}
            <div className="flex items-center justify-between gap-3 px-1">
                <span className="text-xs opacity-60">
                    {netPossible
                        ? (enNet
                            ? `Scores nets, coups rendus déduits${netApproche ? ' · calcul approché' : ''}`
                            : 'Scores bruts, tels que joués')
                        : manqueQuoi}
                </span>
                {netPossible && <BasculeNet net={scoreNet} onChange={setScoreNet} />}
            </div>

            {classes.map((j, rang) => {
                const t = totalDe(j);
                const cestMoi = j.userId === user?.id;
                return (
                    <section key={j.userId}
                        className="rounded-box border border-base-300 bg-base-100 p-3 shadow-sm">
                        <div className="flex items-center gap-3 px-1">
                            {/* Le rang ne s'écrit ici que faute de podium.
                                Quand il y en a un, le classement est déjà
                                dessus, en grand et sur trois marches ; le
                                répéter devant chaque carte ajoute une colonne
                                qui ne dit rien de neuf et décale les visages. */}
                            {carte.players.length > 1 && podium.length < 2 && (
                                <span className="w-5 shrink-0 text-center text-sm font-bold tabular-nums opacity-70">
                                    {t.coups > 0 ? rang + 1 : '–'}
                                </span>
                            )}
                            <Avatar name={j.displayName} avatarUrl={j.avatarUrl} size={36}
                                handicap={j.handicapIndex} />
                            <span className="min-w-0 flex-1 truncate">
                                <span className={`flex items-center gap-2 truncate ${
                                    cestMoi ? 'font-bold' : 'font-semibold opacity-80'}`}>
                                    {cestMoi ? 'vous' : j.displayName}
                                    {/* Le jeton dit ce qu'on peut FAIRE, pas qui
                                        l'on est : sur cette page une seule
                                        carte se touche, et rien ne le disait —
                                        on découvrait la correction en tapant au
                                        hasard sur un trou. */}
                                    {cestMoi && (
                                        <span className="shrink-0 rounded-full bg-base-200 px-2 py-0.5 text-[11px] font-semibold opacity-70">
                                            modifiable
                                        </span>
                                    )}
                                </span>
                                {/* Les COUPS REÇUS, à côté du départ.
                                    C'est le nombre d'où sort tout le net, et il
                                    ne s'affichait nulle part : on lisait un net
                                    sans pouvoir vérifier d'où il venait, ni le
                                    comparer à la carte officielle. « Plafonné »
                                    quand la limite a mordu — sans quoi un index
                                    54 qui reçoit 36 coups reste inexplicable. */}
                                <span className="block text-xs opacity-60">
                                    {j.teeLabel ?? 'départ non précisé'}
                                    {(() => {
                                        const r = rendusDe.get(j.userId);
                                        if (r?.total == null) return null;
                                        return ` · ${r.total} coups reçus${r.plafonne ? ', plafonné' : ''}`;
                                    })()}
                                </span>
                            </span>
                            {/* Le score rendu en GRAND, et sous lui ce qu'il
                                faut pour le situer.

                                En net, c'est le brut qui passe dessous : le net
                                est le score de la partie, le brut est ce qu'on a
                                réellement joué et qu'on retient aussi. En brut,
                                l'écart au par prend cette place — il n'y a rien
                                d'autre à dire. */}
                            <span className="shrink-0 text-right">
                                <span className="block text-lg font-bold leading-none tabular-nums">
                                    {t.coups > 0 ? t.coups : '—'}
                                </span>
                                {enNet && t.brut > 0 ? (
                                    <span className="block text-xs font-semibold tabular-nums opacity-60">
                                        {t.brut} brut
                                    </span>
                                ) : t.par > 0 && (
                                    <span className={`block text-xs font-semibold tabular-nums ${
                                        t.coups > t.par ? 'text-error' : t.coups < t.par ? 'text-success' : 'opacity-60'
                                    }`}>
                                        {t.coups === t.par ? 'par' : `${t.coups > t.par ? '+' : '−'}${Math.abs(t.coups - t.par)}`}
                                    </span>
                                )}
                            </span>
                        </div>

                        {/* Ma carte se touche, celle des autres se regarde. */}
                        <div className="mt-2">
                            <Carte holes={carte.holes} myHoles={j.holes}
                                rendus={rendusDe.get(j.userId)} net={enNet}
                                onChoisir={cestMoi ? (n) => setCorrige(n) : undefined} />
                        </div>
                    </section>
                );
            })}

            {/* Ce que la carte ne dit pas.
                La carte donne les scores, trou par trou : ce qu'on a fait. Elle
                ne dit pas OÙ les coups sont partis — greens pris en régulation,
                départs trouvés, sauvetages, trois-putts. Ce sont les mêmes
                mesures que l'écran des statistiques, bornées à ce tour-là :
                inutile d'en fabriquer une seconde version ici, qui finirait par
                compter autrement. */}
            <Link to={`/statistiques?tour=${carte.round.id}`}
                className="btn btn-block gap-2 border border-base-300 bg-base-100 tap-target">
                <ChartNoAxesColumn size={16} /> Statistiques de ce tour
            </Link>

            {/* Rejouer le même parcours, depuis la carte qu'on vient de rendre.
                C'est là qu'on en a envie — « la prochaine fois je passe ce
                trou » — et le chemin passait sinon par la liste des parcours,
                une recherche, et le choix d'un départ qu'on vient pourtant de
                jouer. Le départ et le sac se choisissent à l'écran suivant : ce
                bouton propose le parcours, pas une partie toute faite. */}
            <Link to={`/parcours/${carte.round.courseId}/jouer`}
                className="btn btn-block gap-2 border border-base-300 bg-base-100 tap-target">
                <Play size={16} /> Rejouer ce parcours
            </Link>

            <button type="button" onClick={() => setConfirmSuppr(true)}
                className="btn btn-ghost btn-block gap-2 text-error tap-target">
                <Trash2 size={16} /> Supprimer ce tour
            </button>

            {/* La correction d'un trou : deux compteurs, rien d'autre. Le club
                et l'issue de chaque coup ne se rejouent pas de mémoire une
                heure après — le SCORE, si. */}
            {trouCorrige && (
                <div className="modal modal-open" role="dialog">
                    <div className="modal-box">
                        <h3 className="text-lg font-bold">
                            Trou {trouCorrige.number}
                            {trouCorrige.par != null && (
                                <span className="ml-2 text-sm font-normal opacity-60">par {trouCorrige.par}</span>
                            )}
                        </h3>

                        {([
                            ['Coups', scoreCorrige?.strokes ?? 0, (v: number) => void ecrire(
                                trouCorrige.number,
                                v === 0 ? null : v,
                                Math.min(scoreCorrige?.putts ?? 0, Math.max(0, v)),
                            )],
                            ['Putts', scoreCorrige?.putts ?? 0, (v: number) => void ecrire(
                                trouCorrige.number,
                                Math.max(scoreCorrige?.strokes ?? 0, v) || null,
                                v,
                            )],
                        ] as const).map(([mot, valeur, poser]) => (
                            <div key={mot} className="mt-4 flex items-center justify-between gap-3">
                                <span className="font-semibold">{mot}</span>
                                <span className="flex items-center gap-3">
                                    <button type="button" disabled={busy || valeur === 0}
                                        onClick={() => poser(valeur - 1)}
                                        aria-label={`Un ${mot.toLowerCase().slice(0, -1)} de moins`}
                                        className="btn btn-outline btn-square h-14 w-14 tap-target">
                                        <Minus size={20} />
                                    </button>
                                    <span className="w-8 text-center text-3xl font-bold tabular-nums">{valeur}</span>
                                    <button type="button" disabled={busy || valeur >= 20}
                                        onClick={() => poser(valeur + 1)}
                                        aria-label={`Un ${mot.toLowerCase().slice(0, -1)} de plus`}
                                        className="btn btn-outline btn-square h-14 w-14 tap-target">
                                        <Plus size={20} />
                                    </button>
                                </span>
                            </div>
                        ))}

                        <div className="modal-action">
                            <button type="button" onClick={() => setCorrige(null)}
                                className="btn btn-primary h-14 tap-target">Terminé</button>
                        </div>
                    </div>
                </div>
            )}

            {confirmSuppr && (
                <div className="modal modal-open" role="dialog">
                    <div className="modal-box">
                        <h3 className="text-lg font-bold">Supprimer ce tour ?</h3>
                        <p className="mt-2 text-sm opacity-80">
                            {carte.players.length > 1
                                ? 'Si vous en êtes l’organisateur, la partie entière disparaît ; sinon, seule votre carte part.'
                                : 'La carte et les coups enregistrés disparaissent.'}
                            {' '}C'est définitif.
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy} onClick={() => void supprimer()}
                                className="btn btn-error btn-block h-14 gap-2 font-bold tap-target">
                                <Trash2 size={16} /> Supprimer
                            </button>
                            <button type="button" onClick={() => setConfirmSuppr(false)}
                                className="btn btn-ghost btn-block tap-target">Garder</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
